import {
  ITile,
  Maybe,
  MoveMoneyVxlTile,
  ProductOfferSingleCardTile,
  Query,
  TileType,
  VxlAction,
} from '@varo/graphql-schema-types';
import { buildMoveMoneyHeaderReactNode } from 'features/moveMoney/components/MoveMoneyHeaderCard/adapters/moveMoneyHeaderCardAdapter';
import {
  buildLeadingAccessory,
  isApplePayRoute,
  isGooglePayRoute,
  renderSectionHeaderBasedOnLabel,
} from 'features/moveMoney/helpers/MoveMoneyTile.helpers';
import {
  isLoadedMoveMoneyVxlTile,
  isMoveMoneyPageSectionTile,
  isProductOfferSingleCardTile,
  isRouteNavigationAction,
  isTag,
  isVxlSectionDivider,
} from 'features/moveMoney/helpers/moveMoneyTileTypeGuards';
import { useCallback } from 'react';
import MoveMoneyPageTileQuery from 'services/graphql/queries/MoveMoney/MoveMoneyPageTileQuery';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { toTagIconKey, toTagType } from 'shared/components/vxl/Tag/Tag.helpers';
import {
  SectionedContentScreenModel,
  SectionedContentScreenSection,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import { CombinedError, useQuery } from 'urql';

interface MoveMoneyPageTileResult {
  moveMoneyPageTiles?: MoveMoneyResponse;
  isLoading: boolean;
  error?: CombinedError;
  reload: () => void;
}

export interface MoveMoneyResponse {
  title?: string;
  sectionList: SectionedContentScreenModel;
}

const moveMoneyPageTileInput = {
  tileType: [
    TileType.MoveMoneyPageSectionTile,
    TileType.MoveMoneyPageTile,
    TileType.MoveMoneyVxlTile,
    TileType.ProductOfferSingleCardTile,
  ],
};

/**
 * Formats Move Money GraphQL data into the SectionedContentScreen model.
 * Inputs:
 * - moveMoneyData: GraphQL Query payload
 * - onPress: handler invoked when a list item is pressed
 * - onHeaderPress: handler invoked for header CTA presses
 * - isNewSparkleTagEnabled: feature flag controlling Apple Pay tag visibility
 * Output:
 * - MoveMoneyResponse or undefined when no data
 * Behavior:
 * - Apple Pay / Google Pay tiles: tag visibility gated by flag
 * - Non–Apple Pay tiles: tags shown as provided
 */
export const formatMoveMoneyData = (
  moveMoneyData: Query,
  onPress: (action: Maybe<VxlAction> | undefined) => void,
  onHeaderPress: () => void,
  isNewSparkleTagEnabled: boolean = true,
): MoveMoneyResponse | undefined => {
  if (!moveMoneyData) {
    return undefined;
  }

  const moveMoneyTilesData = moveMoneyData.moveMoneyPageTile;

  const moveMoneyPageTile = (tile: ITile) => {
    if (!isMoveMoneyPageSectionTile(tile)) {
      return {
        sectionHeader: undefined,
        sectionFooter: undefined,
        data: [],
      };
    }

    const vxlTile = tile;

    const moveMoneyTileData = vxlTile.tiles.map(
      (tile: MoveMoneyVxlTile | ProductOfferSingleCardTile) => {
        if (isLoadedMoveMoneyVxlTile(tile)) {
          if (isVxlSectionDivider(tile.content)) {
            return {
              id: tile.id,
              name: VXLComponentId.LIST_ITEM,
              props: {
                primaryCopy: '',
                secondaryCopy: '',
                showsChevron: false,
              },
            };
          }

          const vxlListItemContent = tile.content;

          const onPressAction = vxlListItemContent.onPressAction;
          const tileRoute = isRouteNavigationAction(onPressAction)
            ? onPressAction.route
            : '';
          const isApplePayTile = isApplePayRoute(tileRoute);
          const isGooglePayTile = isGooglePayRoute(tileRoute);
          const shouldShowTag =
            isApplePayTile || isGooglePayTile ? isNewSparkleTagEnabled : true;

          const trailingAccessory = vxlListItemContent?.trailingAccessory;
          const trailingTag = isTag(trailingAccessory)
            ? trailingAccessory
            : undefined;

          const analyticsAction = vxlListItemContent.onPressAction;

          return {
            id: tile.id,
            name: VXLComponentId.LIST_ITEM,
            props: {
              primaryCopy: vxlListItemContent.primaryText,
              primaryNumberOfLines: 1,
              secondaryCopy: vxlListItemContent.secondaryText,
              secondaryNumberOfLines: 2,
              showsChevron: vxlListItemContent.showsChevron,
              onPress: () => {
                onPress(vxlListItemContent.onPressAction);
              },
              leadingAccessory:
                isApplePayTile || isGooglePayTile
                  ? {
                      id: `${vxlTile.id}-icon`,
                      name: VXLComponentId.ICON_AVATAR,
                      props: {
                        iconKey: isApplePayTile ? 'APPLE_PAY' : 'GOOGLE_PAY',
                        variant: 'LG',
                        backgroundColor: 'transparent',
                      },
                    }
                  : buildLeadingAccessory(vxlListItemContent, vxlTile.id),
              trailingAccessory:
                trailingTag && shouldShowTag
                  ? {
                      id: `${vxlTile.id}-tag`,
                      name: VXLComponentId.TAG,
                      props: {
                        text: trailingTag.text || '',
                        type: toTagType(trailingTag.tagColor) || TagType.INFO,
                        iconKey: toTagIconKey(trailingTag.icon),
                      },
                    }
                  : undefined,
              trailingAccessoryContainerStyle: {
                flexShrink: 0,
              },
              analyticsEvent: isRouteNavigationAction(analyticsAction)
                ? analyticsAction.analyticsEvent
                : undefined,
            },
          };
        }

        return {
          id: tile.id,
          name: VXLComponentId.LIST_ITEM,
          props: {
            primaryCopy: '',
            secondaryCopy: '',
            showsChevron: false,
          },
        };
      },
    );

    const headerTile = vxlTile?.header;
    const headerCard = isProductOfferSingleCardTile(headerTile)
      ? headerTile.card
      : undefined;

    if (!headerCard) {
      return {
        sectionHeader: renderSectionHeaderBasedOnLabel(vxlTile),
        data: moveMoneyTileData,
        sectionFooter: undefined,
      };
    }

    const headerReactNode = buildMoveMoneyHeaderReactNode(
      headerCard,
      onHeaderPress,
    );

    return {
      sectionHeader: headerReactNode,
      data: moveMoneyTileData,
      sectionFooter: undefined,
    };
  };

  const additionalSections: SectionedContentScreenSection[] = moveMoneyTilesData
    ? moveMoneyTilesData.tiles.map(moveMoneyPageTile)
    : [];

  const structuredSectionData = {
    sectionList: {
      sections: [
        {
          sectionHeader: undefined,
          sectionFooter: undefined,
          data: [],
        },
        ...additionalSections,
      ],
    },
  };

  return structuredSectionData;
};

/**
 * Hook: Queries Move Money tiles and returns formatted response.
 * Ensures requestPolicy cache-and-network.
 * Exposes reload() to re-fetch with existing input.
 */
const useMoveMoneyPageTiles = (
  onPress: (action: Maybe<VxlAction> | undefined) => void,
  onHeaderPress: () => void,
): MoveMoneyPageTileResult => {
  const [result, refetch] = useQuery<Query>({
    query: MoveMoneyPageTileQuery,
    variables: { input: moveMoneyPageTileInput },
    requestPolicy: 'cache-and-network',
  });

  const { data, fetching, error } = result;
  const { isFeatureEnabled } = useStatsig();

  const reload = useCallback(() => {
    refetch({
      variables: { input: moveMoneyPageTileInput },
      requestPolicy: 'cache-and-network',
    });
  }, [refetch]);
  const isNewSparkleTagEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_90_DAYS_NEW_SPARKLE_TAG,
  );

  return {
    moveMoneyPageTiles: data?.moveMoneyPageTile
      ? formatMoveMoneyData(
          data,
          onPress,
          onHeaderPress,
          isNewSparkleTagEnabled,
        )
      : undefined,
    isLoading: fetching,
    error,
    reload,
  };
};

export default useMoveMoneyPageTiles;
